import React, { useMemo } from 'react';
import {
  ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceArea,
  ReferenceLine,
} from 'recharts';
import { Thermometer, Gauge, Flame, BatteryCharging, Layers } from 'lucide-react';
import ChartCard from './ChartCard';
import ChartZoomHeader from './ChartZoomHeader';
import { useChartZoom } from './useChartZoom';
import ReferenceLineTopLabel, { REFERENCE_LABEL_TOP_MARGIN, referenceLabelAnchorFor } from './ReferenceLineTopLabel';
import {
  SERIES_COLORS, CHART_GRID, CHART_AXIS, AXIS_TICK_STYLE, AXIS_LABEL_STYLE,
  TOOLTIP_CONTENT_STYLE, TOOLTIP_LABEL_STYLE, LEGEND_STYLE,
} from './chartTheme';
import { STRATEGY_LABELS } from '../../../lib/batteryThermalTools';

const CHART_HEIGHT = 320;
const SOFT_COLOR = SERIES_COLORS[3];
const HARD_COLOR = SERIES_COLORS[7];
const DERATE_COLOR = SERIES_COLORS[7];
export const STRATEGY_COLORS = {
  passive_air: SERIES_COLORS[1],
  forced_air: SERIES_COLORS[3],
  liquid_plate: SERIES_COLORS[2],
  liquid_chiller: SERIES_COLORS[0],
  immersion: SERIES_COLORS[6],
};

const minutes = (s) => s / 60;
const minTick = (v) => Number(v).toFixed(0);
const fmt = (d) => (v) => (v == null ? '—' : Number(v).toFixed(d));
const leftLabel = (value) => ({ value, angle: -90, position: 'insideLeft', style: { ...AXIS_LABEL_STYLE, textAnchor: 'middle' } });
const rightLabel = (value) => ({ value, angle: 90, position: 'insideRight', style: { ...AXIS_LABEL_STYLE, textAnchor: 'middle' } });
const zoomHandlers = (zoom) => ({ onMouseDown: zoom.onMouseDown, onMouseMove: zoom.onMouseMove, onMouseUp: zoom.onMouseUp });
const tooltipLabel = (v) => `t: ${Number(v).toFixed(1)} min`;

function useRows(series) {
  return useMemo(() => {
    if (!series) return [];
    return series.time_s.map((t, i) => ({
      t_min: minutes(t),
      phase: series.phase[i],
      speed_kph: series.speed_kph[i],
      soc_pct: series.soc_pct[i],
      current_a: series.current_a[i],
      core_c: series.core_temp_c[i],
      pack_c: series.pack_temp_c[i],
      coolant_c: series.coolant_temp_c[i],
      requested_kw: series.requested_kw[i],
      delivered_kw: series.delivered_kw[i],
      derate_kw: series.derate_kw[i],
      heat_kw: series.heat_kw[i],
      removed_kw: series.removed_kw[i],
      chiller_kw: series.chiller_kw[i],
      limiter: series.limiter[i],
    }));
  }, [series]);
}

// Time (min) at which the charge phase starts in a drive-then-charge run, else null.
function chargeStartMin(rows) {
  const i = rows.findIndex((r) => r.phase === 'charge');
  return i > 0 ? rows[i].t_min : null;
}

function TimeAxis({ zoom }) {
  return (
    <XAxis
      dataKey="t_min" type="number" domain={zoom.xDomain} allowDataOverflow
      tick={AXIS_TICK_STYLE} axisLine={{ stroke: CHART_GRID }} tickLine={false} tickFormatter={minTick} minTickGap={24}
      label={{ value: 'Time (min)', position: 'insideBottom', offset: -4, style: AXIS_LABEL_STYLE }}
    />
  );
}

function ZoomArea({ zoom, yAxisId }) {
  return zoom.isSelecting ? (
    <ReferenceArea yAxisId={yAxisId} x1={zoom.refAreaLeft} x2={zoom.refAreaRight} stroke={CHART_AXIS} fill={CHART_AXIS} fillOpacity={0.15} />
  ) : null;
}

// Vertical marker where the fast charge begins (drive-then-charge runs only).
function phaseMarker(rows, zoom, yAxisId) {
  const x = chargeStartMin(rows);
  if (x == null) return null;
  const anchor = referenceLabelAnchorFor(x, zoom.xDomain, rows, 't_min');
  return (
    <ReferenceLine yAxisId={yAxisId} x={x} stroke={CHART_AXIS} strokeDasharray="4 3"
      label={<ReferenceLineTopLabel text="Charge starts" anchor={anchor} />} />
  );
}

// Temperature limit lines. A limit outside the data's range would be silently dropped by the
// auto Y axis, so the axis is stretched to include the soft limit once the data gets within
// SOFT_LIMIT_SHOW_WITHIN_K of it, and the hard limit once the soft one is exceeded — far below
// both (e.g. a cold charge) neither is drawn, which keeps the temperature detail readable.
const SOFT_LIMIT_SHOW_WITHIN_K = 10;
const limitLine = (yAxisId, y, color, text, show) => (show ? (
  <ReferenceLine yAxisId={yAxisId} y={y} stroke={color} strokeDasharray="5 4" ifOverflow="extendDomain"
    label={{ value: text, position: 'insideTopRight', fill: color, fontSize: 10, fontFamily: AXIS_LABEL_STYLE.fontFamily }} />
) : null);
const limitLines = (yAxisId, limits, maxTemp) => (
  <>
    {limitLine(yAxisId, limits.soft_limit_c, SOFT_COLOR, `Soft ${limits.soft_limit_c} °C`, maxTemp >= limits.soft_limit_c - SOFT_LIMIT_SHOW_WITHIN_K)}
    {limitLine(yAxisId, limits.hard_limit_c, HARD_COLOR, `Hard ${limits.hard_limit_c} °C`, maxTemp >= limits.soft_limit_c)}
  </>
);
const maxOf = (values) => values.reduce((m, v) => (v != null && v > m ? v : m), -Infinity);

export function TemperatureChart({ series, limits }) {
  const rows = useRows(series);
  const zoom = useChartZoom();
  if (!rows.length) return null;
  return (
    <ChartCard icon={Thermometer} title="Pack Temperatures" testId="chart-thermal-temps"
      description={`Cell core (interior average), pack (cell surface + structure — what the BMS sensors read) and ${limits.coolant_label.toLowerCase()} temperature, against the soft and hard limits.`}>
      <ChartZoomHeader caption="Temperature vs Time" isZoomed={zoom.isZoomed} onReset={zoom.resetZoom} testId="zoom-reset-thermal-temps" />
      <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
        <ComposedChart data={rows} margin={{ top: REFERENCE_LABEL_TOP_MARGIN, right: 8, left: 0, bottom: 4 }} {...zoomHandlers(zoom)}>
          <CartesianGrid stroke={CHART_GRID} vertical={false} />
          <TimeAxis zoom={zoom} />
          <YAxis yAxisId="t" domain={['auto', 'auto']} tick={AXIS_TICK_STYLE} axisLine={{ stroke: CHART_GRID }} tickLine={false} width={48} label={leftLabel('Temp (°C)')} tickFormatter={(v) => Number(v).toFixed(0)} />
          <Tooltip contentStyle={TOOLTIP_CONTENT_STYLE} labelStyle={TOOLTIP_LABEL_STYLE} labelFormatter={tooltipLabel} formatter={fmt(1)} />
          <Legend wrapperStyle={LEGEND_STYLE} />
          {limitLines('t', limits, maxOf(rows.map((r) => r.core_c)))}
          <ReferenceLine yAxisId="t" y={limits.ambient_temp_c} stroke={CHART_AXIS} strokeDasharray="2 3" ifOverflow="extendDomain"
            label={{ value: 'Ambient', position: 'insideBottomLeft', fill: CHART_AXIS, fontSize: 10, fontFamily: AXIS_LABEL_STYLE.fontFamily }} />
          {phaseMarker(rows, zoom, 't')}
          <Line yAxisId="t" type="monotone" dataKey="core_c" name="Cell core" stroke={SERIES_COLORS[7]} dot={false} strokeWidth={1.75} isAnimationActive={false} />
          <Line yAxisId="t" type="monotone" dataKey="pack_c" name="Pack (sensor)" stroke={SERIES_COLORS[1]} dot={false} strokeWidth={1.75} isAnimationActive={false} />
          <Line yAxisId="t" type="monotone" dataKey="coolant_c" name={limits.coolant_label} stroke={SERIES_COLORS[0]} dot={false} strokeWidth={1.5} isAnimationActive={false} />
          <ZoomArea zoom={zoom} yAxisId="t" />
        </ComposedChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

export function PowerDerateChart({ series, profile }) {
  const rows = useRows(series);
  const zoom = useChartZoom();
  if (!rows.length) return null;
  const chargeOnly = profile === 'charge';
  return (
    <ChartCard icon={Gauge} title="Requested vs Delivered Power" testId="chart-thermal-power"
      description={chargeOnly
        ? 'Charger power on offer against the power the pack actually accepted. Red: power withheld by a thermal limit (cold-pack current limit, over-temperature derate, or the thermal system eating the supply).'
        : 'Drive: traction DC power the drivetrain asked for against what the BMS allowed. Charge: charger power on offer against what the pack accepted. Red: power withheld by a temperature/BMS derate.'}>
      <ChartZoomHeader caption="Power vs Time" isZoomed={zoom.isZoomed} onReset={zoom.resetZoom} testId="zoom-reset-thermal-power" />
      <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
        <ComposedChart data={rows} margin={{ top: REFERENCE_LABEL_TOP_MARGIN, right: 8, left: 0, bottom: 4 }} {...zoomHandlers(zoom)}>
          <CartesianGrid stroke={CHART_GRID} vertical={false} />
          <TimeAxis zoom={zoom} />
          <YAxis yAxisId="p" tick={AXIS_TICK_STYLE} axisLine={{ stroke: CHART_GRID }} tickLine={false} width={48} label={leftLabel('Power (kW)')} />
          <Tooltip contentStyle={TOOLTIP_CONTENT_STYLE} labelStyle={TOOLTIP_LABEL_STYLE} labelFormatter={tooltipLabel} formatter={fmt(1)} />
          <Legend wrapperStyle={LEGEND_STYLE} />
          {phaseMarker(rows, zoom, 'p')}
          <Area yAxisId="p" type="stepAfter" dataKey="delivered_kw" name="Delivered" stackId="pw" stroke={SERIES_COLORS[0]} fill={SERIES_COLORS[0]} fillOpacity={0.25} strokeWidth={1.25} isAnimationActive={false} />
          <Area yAxisId="p" type="stepAfter" dataKey="derate_kw" name="Withheld (derate)" stackId="pw" stroke="none" fill={DERATE_COLOR} fillOpacity={0.6} isAnimationActive={false} />
          <Line yAxisId="p" type="stepAfter" dataKey="requested_kw" name="Requested" stroke={SERIES_COLORS[1]} dot={false} strokeWidth={1} isAnimationActive={false} />
          <ZoomArea zoom={zoom} yAxisId="p" />
        </ComposedChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

export function HeatBalanceChart({ series }) {
  const rows = useRows(series);
  const zoom = useChartZoom();
  if (!rows.length) return null;
  return (
    <ChartCard icon={Flame} title="Heat Generation vs Removal" testId="chart-thermal-heat"
      description="Heat generated in the cells (I²R plus the reversible entropic term), heat leaving the pack (to the coolant and the enclosure), and heat the chiller pulls out of the coolant.">
      <ChartZoomHeader caption="Heat Flow vs Time" isZoomed={zoom.isZoomed} onReset={zoom.resetZoom} testId="zoom-reset-thermal-heat" />
      <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
        <ComposedChart data={rows} margin={{ top: REFERENCE_LABEL_TOP_MARGIN, right: 8, left: 0, bottom: 4 }} {...zoomHandlers(zoom)}>
          <CartesianGrid stroke={CHART_GRID} vertical={false} />
          <TimeAxis zoom={zoom} />
          <YAxis yAxisId="q" tick={AXIS_TICK_STYLE} axisLine={{ stroke: CHART_GRID }} tickLine={false} width={48} label={leftLabel('Heat (kW)')} tickFormatter={(v) => Number(v).toFixed(1)} />
          <Tooltip contentStyle={TOOLTIP_CONTENT_STYLE} labelStyle={TOOLTIP_LABEL_STYLE} labelFormatter={tooltipLabel} formatter={fmt(2)} />
          <Legend wrapperStyle={LEGEND_STYLE} />
          {phaseMarker(rows, zoom, 'q')}
          <Line yAxisId="q" type="monotone" dataKey="heat_kw" name="Generated" stroke={SERIES_COLORS[7]} dot={false} strokeWidth={1.25} isAnimationActive={false} />
          <Line yAxisId="q" type="monotone" dataKey="removed_kw" name="Leaving pack" stroke={SERIES_COLORS[0]} dot={false} strokeWidth={1.5} isAnimationActive={false} />
          <Line yAxisId="q" type="stepAfter" dataKey="chiller_kw" name="Chiller" stroke={SERIES_COLORS[6]} dot={false} strokeWidth={1.25} isAnimationActive={false} />
          <ZoomArea zoom={zoom} yAxisId="q" />
        </ComposedChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

export function SocCurrentChart({ series }) {
  const rows = useRows(series);
  const zoom = useChartZoom();
  if (!rows.length) return null;
  return (
    <ChartCard icon={BatteryCharging} title="SoC and Pack Current" testId="chart-thermal-soc"
      description="State of charge and pack current (positive = discharge, negative = charge).">
      <ChartZoomHeader caption="SoC & Current vs Time" isZoomed={zoom.isZoomed} onReset={zoom.resetZoom} testId="zoom-reset-thermal-soc" />
      <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
        <ComposedChart data={rows} margin={{ top: REFERENCE_LABEL_TOP_MARGIN, right: 8, left: 0, bottom: 4 }} {...zoomHandlers(zoom)}>
          <CartesianGrid stroke={CHART_GRID} vertical={false} />
          <TimeAxis zoom={zoom} />
          <YAxis yAxisId="soc" domain={[0, 100]} tick={AXIS_TICK_STYLE} axisLine={{ stroke: CHART_GRID }} tickLine={false} width={44} label={leftLabel('SoC (%)')} />
          <YAxis yAxisId="i" orientation="right" tick={AXIS_TICK_STYLE} axisLine={{ stroke: CHART_GRID }} tickLine={false} width={52} label={rightLabel('Current (A)')} tickFormatter={(v) => Number(v).toFixed(0)} />
          <Tooltip contentStyle={TOOLTIP_CONTENT_STYLE} labelStyle={TOOLTIP_LABEL_STYLE} labelFormatter={tooltipLabel} formatter={fmt(1)} />
          <Legend wrapperStyle={LEGEND_STYLE} />
          {phaseMarker(rows, zoom, 'soc')}
          <Line yAxisId="i" type="monotone" dataKey="current_a" name="Current (A)" stroke={SERIES_COLORS[2]} dot={false} strokeWidth={1} isAnimationActive={false} />
          <Line yAxisId="soc" type="monotone" dataKey="soc_pct" name="SoC (%)" stroke={SERIES_COLORS[0]} dot={false} strokeWidth={1.75} isAnimationActive={false} />
          <ZoomArea zoom={zoom} yAxisId="soc" />
        </ComposedChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

export function StrategyOverlayChart({ comparison, limits }) {
  const rows = useMemo(() => comparison.time_s.map((t, i) => {
    const row = { t_min: minutes(t) };
    for (const [k, arr] of Object.entries(comparison.core_temp_c)) row[k] = arr[i];
    return row;
  }), [comparison]);
  const zoom = useChartZoom();
  if (!rows.length) return null;
  const selected = comparison.rows.find((r) => r.selected)?.strategy;
  return (
    <ChartCard icon={Layers} title="Cooling Strategy Comparison" testId="chart-thermal-strategies"
      description="Cell core temperature for every cooling strategy on the same inputs (your edited values for the selected one, presets scaled to this pack for the rest). A line ends early when that run's charge finished sooner.">
      <ChartZoomHeader caption="Core Temperature vs Time" isZoomed={zoom.isZoomed} onReset={zoom.resetZoom} testId="zoom-reset-thermal-strategies" />
      <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
        <ComposedChart data={rows} margin={{ top: REFERENCE_LABEL_TOP_MARGIN, right: 8, left: 0, bottom: 4 }} {...zoomHandlers(zoom)}>
          <CartesianGrid stroke={CHART_GRID} vertical={false} />
          <TimeAxis zoom={zoom} />
          <YAxis yAxisId="t" domain={['auto', 'auto']} tick={AXIS_TICK_STYLE} axisLine={{ stroke: CHART_GRID }} tickLine={false} width={48} label={leftLabel('Core (°C)')} tickFormatter={(v) => Number(v).toFixed(0)} />
          <Tooltip contentStyle={TOOLTIP_CONTENT_STYLE} labelStyle={TOOLTIP_LABEL_STYLE} labelFormatter={tooltipLabel} formatter={fmt(1)} />
          <Legend wrapperStyle={LEGEND_STYLE} />
          {limitLines('t', limits, maxOf(Object.values(comparison.core_temp_c).flat()))}
          {Object.keys(comparison.core_temp_c).map((k) => (
            <Line key={k} yAxisId="t" type="monotone" dataKey={k} name={STRATEGY_LABELS[k] || k}
              stroke={STRATEGY_COLORS[k]} dot={false} strokeWidth={k === selected ? 2.5 : 1.25}
              connectNulls={false} isAnimationActive={false} />
          ))}
          <ZoomArea zoom={zoom} yAxisId="t" />
        </ComposedChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}
